import React from "react";
import {
  MDBBtn,
  MDBIcon,
  MDBSpinner,
  MDBModal,
  MDBModalDialog,
  MDBModalContent,
  MDBModalHeader,
  MDBModalTitle,
  MDBModalBody,
  MDBModalFooter,
  MDBInput,
  MDBRow,
  MDBCol,
} from "mdb-react-ui-kit";

import { doFetchJson } from "Projects/CC_cubi_clan_project/Utils/ConnectionManager";
import { toast } from "Projects/CC_cubi_clan_project/GlobalComponents/ToastHost/ToastHost";
import VenueEditor from "./VenueEditor";

const EMPTY_FORM = { name: "", address: "", gridRows: "10", gridCols: "15" };

/**
 * VenueList — lista de salas con CRUD y editor de plano.
 *
 * Props:
 *  - (none — self-contained)
 */
class VenueList extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      venues: [],
      loading: false,
      // form modal
      formOpen: false,
      editVenue: null,
      form: { ...EMPTY_FORM },
      saving: false,
      // confirm delete
      confirmDelete: null,
      deleting: false,
      // editor
      editorVenue: null,
    };
  }

  componentDidMount() {
    this.loadVenues();
  }

  // ─── data ─────────────────────────────────────────────────────────────────

  loadVenues = () => {
    this.setState({ loading: true });
    doFetchJson("TEAN", "getVenues")
      .then((json) => {
        if (json && json.STATE === "SUCCESS") {
          this.setState({ venues: json.DATA || [] });
        } else {
          toast.show({ message: (json && json.MESSAGE) || "Error al cargar salas", type: "danger" });
        }
      })
      .catch(() => toast.show({ message: "Error de red", type: "danger" }))
      .finally(() => this.setState({ loading: false }));
  };

  // ─── form modal ───────────────────────────────────────────────────────────

  openNew = () => {
    this.setState({ formOpen: true, editVenue: null, form: { ...EMPTY_FORM } });
  };

  openEdit = (venue) => {
    this.setState({
      formOpen: true,
      editVenue: venue,
      form: {
        name: venue.name || "",
        address: venue.address || "",
        gridRows: String(venue.gridRows || 10),
        gridCols: String(venue.gridCols || 15),
      },
    });
  };

  closeForm = () => {
    this.setState({ formOpen: false, editVenue: null, form: { ...EMPTY_FORM } });
  };

  handleFormChange = (field) => (e) => {
    this.setState((prev) => ({ form: { ...prev.form, [field]: e.target.value } }));
  };

  handleSave = () => {
    const { editVenue, form } = this.state;
    const name = form.name.trim();
    if (!name) {
      toast.show({ message: "El nombre de la sala es obligatorio", type: "warning" });
      return;
    }
    const gridRows = parseInt(form.gridRows, 10);
    const gridCols = parseInt(form.gridCols, 10);
    if (isNaN(gridRows) || isNaN(gridCols) || gridRows < 1 || gridCols < 1) {
      toast.show({ message: "Las dimensiones deben ser números positivos", type: "warning" });
      return;
    }
    if (gridRows > 50 || gridCols > 50) {
      toast.show({ message: "Máximo 50 filas y 50 columnas", type: "warning" });
      return;
    }

    const dto = {
      idVenue: editVenue ? editVenue.idVenue : null,
      name,
      address: form.address.trim() || null,
      gridRows,
      gridCols,
    };
    const endpoint = editVenue ? "updateVenue" : "createVenue";
    const method = editVenue ? "PUT" : "POST";

    this.setState({ saving: true });
    doFetchJson("TEAN", endpoint, { method, headers: { "Content-Type": "application/json" }, body: JSON.stringify(dto) })
      .then((json) => {
        if (json && json.STATE === "SUCCESS") {
          toast.show({ message: editVenue ? "Sala actualizada" : "Sala creada", type: "success" });
          this.closeForm();
          this.loadVenues();
        } else {
          toast.show({ message: (json && json.MESSAGE) || "Error al guardar la sala", type: "danger" });
        }
      })
      .catch(() => toast.show({ message: "Error de red", type: "danger" }))
      .finally(() => this.setState({ saving: false }));
  };

  // ─── delete ───────────────────────────────────────────────────────────────

  askDelete = (venue) => this.setState({ confirmDelete: venue });
  cancelDelete = () => this.setState({ confirmDelete: null });

  confirmDeleteNow = () => {
    const { confirmDelete } = this.state;
    if (!confirmDelete) return;
    this.setState({ deleting: true });
    doFetchJson("TEAN", "deleteVenue/" + confirmDelete.idVenue, { method: "DELETE", headers: { "Content-Type": "application/json" } })
      .then((json) => {
        if (json && json.STATE === "SUCCESS") {
          toast.show({ message: "Sala eliminada", type: "success" });
          this.setState({ confirmDelete: null });
          this.loadVenues();
        } else {
          toast.show({ message: (json && json.MESSAGE) || "Error al eliminar la sala", type: "danger" });
        }
      })
      .catch(() => toast.show({ message: "Error de red", type: "danger" }))
      .finally(() => this.setState({ deleting: false }));
  };

  // ─── editor ───────────────────────────────────────────────────────────────

  openEditor = (venue) => {
    // Cargar el venue completo (con zones + seats)
    doFetchJson("TEAN", "getVenue/" + venue.idVenue)
      .then((json) => {
        if (json && json.STATE === "SUCCESS") {
          this.setState({ editorVenue: json.DATA });
        } else {
          toast.show({ message: "No se pudo cargar la sala", type: "danger" });
        }
      })
      .catch(() => toast.show({ message: "Error de red", type: "danger" }));
  };

  closeEditor = () => this.setState({ editorVenue: null });

  onVenueChangedInEditor = (updated) => {
    this.setState((prev) => ({
      venues: prev.venues.map((v) => v.idVenue === updated.idVenue ? { ...v, ...updated } : v),
    }));
  };

  // ─── render ───────────────────────────────────────────────────────────────

  renderTable() {
    const { venues } = this.state;
    if (!venues.length) {
      return (
        <div className="text-muted fst-italic py-3 text-center">
          No hay salas registradas. Crea la primera con el botón de arriba.
        </div>
      );
    }
    return (
      <div className="table-responsive mt-3">
        <table className="table table-sm table-hover align-middle">
          <thead className="table-light">
            <tr>
              <th>#</th>
              <th>Nombre</th>
              <th>Dirección</th>
              <th>Cuadrícula</th>
              <th>Zonas</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {venues.map((v) => (
              <tr key={v.idVenue}>
                <td className="text-muted small">{v.idVenue}</td>
                <td className="fw-semibold">{v.name}</td>
                <td className="text-muted small">{v.address || "—"}</td>
                <td className="text-muted small">{v.gridRows} × {v.gridCols}</td>
                <td className="text-muted small">{Array.isArray(v.zones) ? v.zones.length : "—"}</td>
                <td>
                  <div className="d-flex gap-1 justify-content-end">
                    <MDBBtn
                      size="sm"
                      color="info"
                      onClick={() => this.openEditor(v)}
                      title="Editar plano"
                    >
                      <MDBIcon fas icon="th" className="me-1" />
                      Plano
                    </MDBBtn>
                    <MDBBtn
                      size="sm"
                      color="secondary"
                      onClick={() => this.openEdit(v)}
                      title="Editar datos"
                    >
                      <MDBIcon fas icon="pen" />
                    </MDBBtn>
                    <MDBBtn
                      size="sm"
                      color="danger"
                      onClick={() => this.askDelete(v)}
                      title="Eliminar sala"
                    >
                      <MDBIcon fas icon="trash" />
                    </MDBBtn>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  render() {
    const { loading, formOpen, editVenue, form, saving, confirmDelete, deleting, editorVenue } = this.state;

    // Si hay un venue abierto en el editor, mostrarlo en lugar de la lista
    if (editorVenue) {
      return (
        <div>
          <div className="d-flex align-items-center mb-3">
            <MDBBtn size="sm" color="light" onClick={this.closeEditor} className="me-3">
              <MDBIcon fas icon="arrow-left" className="me-1" />
              Volver a salas
            </MDBBtn>
            <span className="fw-semibold fs-6">Plano de sala: {editorVenue.name}</span>
          </div>
          <VenueEditor venue={editorVenue} onVenueChanged={this.onVenueChangedInEditor} />
        </div>
      );
    }

    return (
      <div>
        {/* Header */}
        <div className="d-flex align-items-center justify-content-between mb-2">
          <div className="fw-semibold">Salas</div>
          <div className="d-flex gap-2">
            <MDBBtn size="sm" color="light" onClick={this.loadVenues} disabled={loading} title="Recargar">
              <MDBIcon fas icon="sync-alt" />
            </MDBBtn>
            <MDBBtn size="sm" color="primary" onClick={this.openNew}>
              <MDBIcon fas icon="plus" className="me-1" />
              Nueva sala
            </MDBBtn>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-4"><MDBSpinner /></div>
        ) : (
          this.renderTable()
        )}

        {/* Form modal */}
        <MDBModal show={formOpen} onHide={this.closeForm} tabIndex="-1">
          <MDBModalDialog centered>
            <MDBModalContent>
              <MDBModalHeader>
                <MDBModalTitle>
                  <MDBIcon fas icon="building" className="me-2" />
                  {editVenue ? "Editar sala" : "Nueva sala"}
                </MDBModalTitle>
                <MDBBtn className="btn-close" color="none" onClick={this.closeForm} />
              </MDBModalHeader>
              <MDBModalBody>
                <MDBRow className="g-3">
                  <MDBCol size="12">
                    <MDBInput
                      label="Nombre *"
                      value={form.name}
                      onChange={this.handleFormChange("name")}
                    />
                  </MDBCol>
                  <MDBCol size="12">
                    <MDBInput
                      label="Dirección"
                      value={form.address}
                      onChange={this.handleFormChange("address")}
                    />
                  </MDBCol>
                  <MDBCol size="6">
                    <MDBInput
                      label="Filas"
                      type="number"
                      min="1"
                      max="50"
                      value={form.gridRows}
                      onChange={this.handleFormChange("gridRows")}
                    />
                  </MDBCol>
                  <MDBCol size="6">
                    <MDBInput
                      label="Columnas"
                      type="number"
                      min="1"
                      max="50"
                      value={form.gridCols}
                      onChange={this.handleFormChange("gridCols")}
                    />
                  </MDBCol>
                  {editVenue && (
                    <MDBCol size="12">
                      <div className="alert alert-warning py-2 small mb-0">
                        <MDBIcon fas icon="exclamation-triangle" className="me-1" />
                        Cambiar las dimensiones puede afectar a las zonas existentes si reducen el tamaño.
                      </div>
                    </MDBCol>
                  )}
                </MDBRow>
              </MDBModalBody>
              <MDBModalFooter>
                <MDBBtn color="secondary" outline onClick={this.closeForm} disabled={saving}>
                  Cancelar
                </MDBBtn>
                <MDBBtn color="primary" onClick={this.handleSave} disabled={saving}>
                  {saving ? <span className="spinner-border spinner-border-sm me-1" /> : <MDBIcon fas icon="save" className="me-1" />}
                  {editVenue ? "Guardar cambios" : "Crear sala"}
                </MDBBtn>
              </MDBModalFooter>
            </MDBModalContent>
          </MDBModalDialog>
        </MDBModal>

        {/* Confirm delete */}
        {confirmDelete && (
          <div className="venue-confirm-overlay">
            <div className="venue-confirm-box">
              <div className="fw-semibold mb-2">
                <MDBIcon fas icon="exclamation-triangle" className="text-danger me-2" />
                ¿Eliminar sala "{confirmDelete.name}"?
              </div>
              <div className="text-muted small mb-3">
                Se eliminarán todas las zonas y butacas asociadas. Las funciones vinculadas perderán la sala asignada.
              </div>
              <div className="d-flex gap-2 justify-content-end">
                <MDBBtn color="secondary" outline size="sm" onClick={this.cancelDelete} disabled={deleting}>
                  Cancelar
                </MDBBtn>
                <MDBBtn color="danger" size="sm" onClick={this.confirmDeleteNow} disabled={deleting}>
                  {deleting ? <span className="spinner-border spinner-border-sm me-1" /> : <MDBIcon fas icon="trash" className="me-1" />}
                  Eliminar
                </MDBBtn>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
}

export default VenueList;
